import { useState } from 'react';
import { Braces, ListChecks, SlidersHorizontal } from 'lucide-react';
import type { IssueField, IssueForm } from '../domain/types';
import type { ValidationIssue } from '../domain/validation';
import type { Language, TranslationKey } from '../i18n';
import { InspectorPanel } from '../features/editor/InspectorPanel';
import { ValidationPanel } from '../features/validation/ValidationPanel';
import { YamlPanel } from '../features/yaml/YamlPanel';
import { Tabs } from './Tabs';
import styles from './RightPanel.module.css';

type RightTab = 'properties' | 'yaml' | 'validation';

interface RightPanelProps {
  form: IssueForm;
  selectedField: IssueField | null;
  yaml: string;
  copied: boolean;
  issues: ValidationIssue[];
  language: Language;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onFormChange: (form: IssueForm) => void;
  onFieldChange: (field: IssueField) => void;
  onCopy: () => void;
  onDownload: () => void;
  onIssueClick: (issue: ValidationIssue) => void;
}

export function RightPanel({
  form,
  selectedField,
  yaml,
  copied,
  issues,
  language,
  t,
  onFormChange,
  onFieldChange,
  onCopy,
  onDownload,
  onIssueClick,
}: RightPanelProps) {
  const [tab, setTab] = useState<RightTab>('yaml');

  return (
    <aside className={styles.panel} aria-label={t('properties')}>
      <Tabs
        label={t('properties')}
        value={tab}
        onChange={setTab}
        options={[
          {
            id: 'properties',
            label: t('properties'),
            icon: <SlidersHorizontal size={14} strokeWidth={1.8} aria-hidden="true" />,
          },
          {
            id: 'yaml',
            label: t('yaml'),
            icon: <Braces size={14} strokeWidth={1.8} aria-hidden="true" />,
          },
          {
            id: 'validation',
            label: t('validation'),
            icon: <ListChecks size={14} strokeWidth={1.8} aria-hidden="true" />,
          },
        ]}
      />
      <div className={styles.content}>
        {tab === 'properties' ? (
          <InspectorPanel
            form={form}
            selectedField={selectedField}
            t={t}
            onFormChange={onFormChange}
            onFieldChange={onFieldChange}
          />
        ) : null}
        {tab === 'yaml' ? (
          <YamlPanel yaml={yaml} copied={copied} t={t} onCopy={onCopy} onDownload={onDownload} />
        ) : null}
        {tab === 'validation' ? (
          <ValidationPanel issues={issues} language={language} t={t} onIssueClick={onIssueClick} />
        ) : null}
      </div>
      {tab !== 'validation' ? (
        <ValidationPanel
          compact
          issues={issues}
          language={language}
          t={t}
          onIssueClick={onIssueClick}
        />
      ) : null}
    </aside>
  );
}
